import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

const NO_OWNER = '__none__';

export function AdminCardEditPage() {
  const { id: idParam } = useParams<{ id?: string }>();
  const isNew = !idParam;
  const id = idParam ? parseInt(idParam, 10) : null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [bankCode, setBankCode] = useState<'VCB' | 'ACB' | 'VPB'>('VCB');
  const [last4, setLast4] = useState('');
  const [alias, setAlias] = useState('');
  const [status, setStatus] = useState<'active' | 'frozen' | 'closed'>('active');
  const [limit, setLimit] = useState('');
  const [owningDeptId, setOwningDeptId] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin-cards'],
    queryFn: () => api.listCards(),
    enabled: !isNew,
  });
  const existing = !isNew && listData ? listData.rows.find((c) => c.id === id) : undefined;

  const { data: deptsResp } = useQuery({
    queryKey: ['admin-departments'],
    queryFn: () => api.listDepartments(),
  });

  useEffect(() => {
    if (existing) {
      setBankCode(existing.bank_code as 'VCB' | 'ACB' | 'VPB');
      setLast4(existing.last4);
      setAlias(existing.alias);
      setStatus(existing.status);
      setLimit(existing.monthly_limit_vnd?.toString() ?? '');
      setOwningDeptId(existing.owning_department_id);
    }
  }, [existing]);

  const create = useMutation({
    mutationFn: (body: {
      bank_code: string;
      last4: string;
      alias: string;
      monthly_limit_vnd: number | null;
      owning_department_id: number | null;
    }) => api.createCard(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cards'] });
      qc.invalidateQueries({ queryKey: ['cards-lookup'] });
      navigate('/admin/cards');
    },
    onError: (e) => setServerError(String(e).slice(0, 200)),
  });
  const update = useMutation({
    mutationFn: (body: {
      alias?: string;
      status?: 'active' | 'frozen' | 'closed';
      monthly_limit_vnd?: number | null;
      owning_department_id?: number | null;
    }) => api.updateCard(id as number, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cards'] });
      qc.invalidateQueries({ queryKey: ['cards-lookup'] });
      navigate('/admin/cards');
    },
    onError: (e) => setServerError(String(e).slice(0, 200)),
  });

  const pending = create.isPending || update.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (isNew) {
      create.mutate({
        bank_code: bankCode,
        last4,
        alias: alias.trim(),
        monthly_limit_vnd: limit ? Number(limit) : null,
        owning_department_id: owningDeptId,
      });
    } else {
      update.mutate({
        alias: alias.trim(),
        status,
        monthly_limit_vnd: limit ? Number(limit) : null,
        owning_department_id: owningDeptId,
      });
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Link
        to="/admin/cards"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-card hover:text-foreground"
      >
        <ArrowLeft size={16} strokeWidth={1.5} />
        Back to list
      </Link>
      <PageHeader title={isNew ? 'Add card' : `Edit ${existing ? existing.alias : `#${id}`}`} />

      {!isNew && listLoading && <Skeleton className="h-40 w-full" />}

      {(isNew || existing) && (
        <Card>
          <form onSubmit={onSubmit} className="space-y-3 p-4 text-sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="bank">Bank</Label>
                <Select
                  value={bankCode}
                  onValueChange={(v) => setBankCode(v as 'VCB' | 'ACB' | 'VPB')}
                  disabled={!isNew}
                >
                  <SelectTrigger id="bank">
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VCB">Vietcombank</SelectItem>
                    <SelectItem value="ACB">ACB</SelectItem>
                    <SelectItem value="VPB">VPBank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="last4">Last 4 digits</Label>
                <Input
                  id="last4"
                  value={last4}
                  onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  disabled={!isNew}
                  placeholder="8653"
                  required
                  pattern="\d{4}"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alias">Alias</Label>
              <Input
                id="alias"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="CEO Visa"
                required
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {!isNew && (
                <div className="space-y-1.5">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(v) => setStatus(v as 'active' | 'frozen' | 'closed')}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">active</SelectItem>
                      <SelectItem value="frozen">frozen</SelectItem>
                      <SelectItem value="closed">closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className={`space-y-1.5 ${isNew ? 'sm:col-span-2' : ''}`}>
                <Label htmlFor="limit">Monthly limit VND (tuỳ chọn)</Label>
                <Input
                  id="limit"
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder="50000000"
                  min="0"
                  step="1"
                  className="tabular-nums"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="owning-dept">Owning department</Label>
              <Select
                value={owningDeptId === null ? NO_OWNER : owningDeptId.toString()}
                onValueChange={(v) => setOwningDeptId(v === NO_OWNER ? null : parseInt(v, 10))}
              >
                <SelectTrigger id="owning-dept">
                  <SelectValue placeholder="Chưa gán" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OWNER}>Chưa gán</SelectItem>
                  {deptsResp?.rows?.map((d) => (
                    <SelectItem key={d.id} value={d.id.toString()}>
                      <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
                      <span className="ml-2">{d.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Tất cả giao dịch mới của thẻ sẽ được auto-assign vào dept này. Giao dịch cũ giữ
                nguyên.
              </p>
            </div>

            {serverError && (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                {serverError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => navigate('/admin/cards')}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" variant="default" size="sm" disabled={pending}>
                {pending ? 'Đang lưu…' : isNew ? 'Create' : 'Save'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
